import { useCallback, useMemo } from 'react';
import {
  Button,
  ExportWallet,
  ExportWalletCopyAddress,
  ExportWalletCopyKeyButton,
  ExportWalletFooter,
  ExportWalletModal,
  ExportWalletModalContent,
  ExportWalletProps,
  ExportWalletTitle,
  ExportWalletWarning,
  IconXMark,
  useAppConfig,
  useVerifyMfaInlineContext,
  VerifyMfaInline,
  VerifyMfaInlineBackButton,
  VerifyMfaInlineFlow,
} from '@coinbase/cdp-react';
import { Banner } from '@coinbase/cdp-react/components/ui/Banner';
import { useUser } from 'apps/cdp-web-demo/app/(main)/UserContext';
import { useBreakpoints } from '@cbhq/cds-web/hooks/useBreakpoints';

import { Dialog, DialogClose } from '../Dialog/Dialog';

import styles from './ExportWalletDialog.module.css';

type ExportWalletDialogProps = Omit<ExportWalletProps, 'children' | 'skipMfa'>;

function CloseButton() {
  return (
    <DialogClose asChild>
      <Button
        className={styles['close-button']}
        aria-label="Close"
        size="md"
        variant="transparentSecondary"
      >
        <IconXMark className={styles['close-icon']} />
      </Button>
    </DialogClose>
  );
}

function Header() {
  const { view } = useVerifyMfaInlineContext();
  return (
    <div className={styles.header}>
      {view !== 'content' && <VerifyMfaInlineBackButton />}
      {view === 'content' && (
        <ExportWalletTitle>Copy private key to export wallet</ExportWalletTitle>
      )}
      <CloseButton />
    </div>
  );
}

export function ExportWalletDialog({
  onCopySuccess,
  className = '',
  ...props
}: ExportWalletDialogProps) {
  const { showCoinbaseFooter } = useAppConfig();
  const { loggedInView, setLoggedInView } = useUser();
  const { isTablet, isDesktop } = useBreakpoints();
  const isWideLayout = isTablet || isDesktop;
  const isOpen = loggedInView === 'export-wallet';

  const themeOverrides = useMemo(() => {
    return {
      pageBg: 'transparent',
    };
  }, []);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        setLoggedInView(null);
      } else {
        setLoggedInView('export-wallet');
      }
    },
    [setLoggedInView],
  );

  return (
    <>
      {isWideLayout && (
        <Dialog
          open={isOpen}
          onOpenChange={handleOpenChange}
          title="Export your wallet"
          className={styles['export-wallet-dialog']}
        >
          <VerifyMfaInline
            verifyFirst
            className={`${styles.wrapper} ${!showCoinbaseFooter ? styles['wrapper-no-footer'] : ''}`}
          >
            <Header />
            <VerifyMfaInlineFlow transition="fade">
              <ExportWallet
                className={`${className} ${styles['export-wallet']}`}
                onCopySuccess={onCopySuccess}
                skipMfa
                {...props}
              >
                {({ isSessionExpired }) => (
                  <>
                    {isSessionExpired ? (
                      <Banner role="alert" data-part="warning" variant="warning">
                        Your export session has expired. Please close and reopen the dialog to
                        export again.
                      </Banner>
                    ) : (
                      <ExportWalletWarning />
                    )}
                    <ExportWalletCopyAddress />
                    <ExportWalletCopyKeyButton theme={themeOverrides} />
                  </>
                )}
              </ExportWallet>
            </VerifyMfaInlineFlow>
            {showCoinbaseFooter && <ExportWalletFooter className={styles.footer} />}
          </VerifyMfaInline>
        </Dialog>
      )}
      {!isWideLayout && (
        <ExportWalletModal
          open={loggedInView === 'export-wallet'}
          setIsOpen={handleOpenChange}
          onCopySuccess={onCopySuccess}
          {...props}
        >
          <ExportWalletModalContent />
        </ExportWalletModal>
      )}
    </>
  );
}
